import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBoxOpen,
  faChevronDown,
  faDroplet,
  faHandHoldingMedical,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { MEDICAL_SUPPLY_DEFINITIONS } from '../data/medicalSupplies'
import type { MedicalSupplyDefinition } from '../data/medicalSupplies'
import { whatsAppCtaProps } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { getStorefrontConfig, listStorefrontCategories, toExternalImage } from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig } from '../lib/dropify'
import {
  INDEXABLE_ROBOTS,
  absoluteUrl,
  brandTitle,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-latex-nitrile'
const PAGE_CATEGORY = 'Sarung Tangan Latex Nitrile'

const CARD_IMAGE_TRANSFORM = { width: 560, height: 560, fitIn: true, format: 'webp', quality: 74 } as const

const GLOVES = MEDICAL_SUPPLY_DEFINITIONS
const NITRILE_GLOVES = GLOVES.filter((item) => item.material === 'Nitrile')
const LATEX_GLOVES = GLOVES.filter((item) => item.material === 'Latex')

// Two decisions cover most buyers: bahan dulu, lalu bedak. The comparison
// copy sticks to standard product knowledge; per-SKU documents stay with
// the admin because they differ per product.
const MATERIAL_GUIDE = [
  {
    icon: faHandHoldingMedical,
    title: 'Latex',
    points: [
      'Elastis dan mengikuti bentuk tangan.',
      'Merek yang tercantum di halaman ini meliputi NAYA, Otoro, Shamrock, dan lini Disposable.',
      'Protein lateks dapat memicu reaksi pada sebagian pemakai yang sensitif.',
    ],
  },
  {
    icon: faDroplet,
    title: 'Nitrile',
    points: [
      'Bebas protein lateks, jadi alternatif ketika pemakainya sensitif terhadap latex.',
      'Kompatibilitas terhadap minyak, bahan kimia, dan risiko sobek perlu diperiksa pada dokumen setiap SKU.',
      'Tersedia Shamrock powder free serta lini Disposable warna biru dan hitam.',
    ],
  },
]

const TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Klinik kami habis stok tiap bulan. Bisakah pesan rutin?',
    a: 'Bisa. Simpan nama produk dan ukuran dari pesanan pertama, lalu kirim daftar yang sama tiap kali restock. Admin mengecek stok dan mengirim quotation baru setiap kali.',
  },
  {
    q: 'Butuh puluhan box sekaligus. Stoknya sanggup?',
    a: 'Sebutkan produk, ukuran, dan total box yang direncanakan. Admin memeriksa stok dan mengonfirmasi jumlah yang tersedia untuk pesanan tersebut.',
  },
  {
    q: 'Saya belum pernah pakai merek Shamrock, NAYA, atau Otoro. Bagaimana menilainya?',
    a: 'Minta foto barang dan kemasan aktual dari admin sebelum membayar. Untuk mencoba lebih dulu dalam jumlah kecil, tanyakan opsi sample ke admin.',
  },
  {
    q: 'Apakah warna hitam tersedia untuk latex dan nitrile?',
    a: 'Ada. Shamrock Latex Powder Free hitam berisi 50 pcs per box, dan Disposable Nitrile Powder Free hitam berisi 100 pcs. Warna lain: biru untuk nitrile, pink dan putih untuk latex.',
  },
  {
    q: 'Toko alkes saya perlu jual beberapa varian. Bisa campur dalam satu order?',
    a: 'Bisa. Gabungkan latex dan nitrile, powdered dan powder free, dalam satu daftar. Minimum order 10 box dihitung dari total pesanan, konfirmasi kombinasinya ke admin.',
  },
]

export const LATEX_NITRILE_FAQ: FaqItem[] = [
  {
    q: 'Berapa minimum order dan isi per box?',
    a: 'Minimum order 10 box. Hampir semua produk berisi 100 pcs per box; Shamrock Latex Powder Free Black berisi 50 pcs per box.',
  },
  {
    q: 'Bagaimana memilih latex atau nitrile?',
    a: 'Latex bersifat elastis dan mengikuti bentuk tangan, tetapi protein lateks dapat memicu reaksi pada sebagian pemakai. Nitrile tidak mengandung protein lateks. Untuk paparan minyak atau bahan kimia, periksa kompatibilitas dan dokumen SKU sebelum membeli.',
  },
  {
    q: 'Apa beda powdered dan powder free?',
    a: 'Powdered memakai bedak agar sarung tangan mudah dipakai dan dilepas. Powder free dipilih ketika residu bedak mengganggu pekerjaan atau pemakainya. Stok kami mencakup keduanya untuk latex; nitrile tersedia powder free.',
  },
  {
    q: 'Ukuran apa saja yang tersedia?',
    a: 'Ketersediaan ukuran berbeda per produk dan stok. Sebutkan ukuran yang dibutuhkan, misalnya S, M, atau L, saat menghubungi admin agar stok per ukurannya dicek.',
  },
  {
    q: 'Apakah ada dokumen untuk kebutuhan pengadaan?',
    a: 'Dokumen berbeda per produk. Sampaikan syarat dari instansi atau tim pengadaan Anda, admin memeriksa dokumen yang menyertai produk sebelum PO dibuat.',
  },
  {
    q: 'Apa bedanya dengan sarung tangan Gekko Grip?',
    a: 'Produk di halaman ini sekali pakai dan dijual per box. Gekko Grip adalah sarung tangan kerja rajut berlapis nitrile yang dipakai berulang dan dijual per lusin. Untuk kerja bengkel dan proyek, lihat halaman sarung tangan Gekko.',
  },
]

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  className,
}: {
  message: string
  label: string
  placement: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', { cta_placement: placement, lp_category: PAGE_CATEGORY })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function GloveCard({ glove }: { glove: MedicalSupplyDefinition }) {
  const message = `Halo Dropify, saya mau cek ${glove.name}. Ukuran: ___. Kebutuhan: ___ box. Kota tujuan: ___. Bisa kirim harga per box dan stoknya?`
  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-sky-100 bg-white">
      <a href={`/produk/${glove.slug}`} className="block bg-sky-50/60">
        <img
          src={toExternalImage(glove.image, CARD_IMAGE_TRANSFORM)}
          alt={glove.name}
          width="560"
          height="560"
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-contain"
        />
      </a>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-sky-900">{glove.material}</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.powder}</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.packSize} pcs/box</span>
          {glove.color && (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-slate-600">{glove.color}</span>
          )}
        </div>
        <h3 className="mt-3 flex-1 text-base font-black tracking-tight text-slate-900">
          <a href={`/produk/${glove.slug}`} className="hover:text-sky-700">{glove.name}</a>
        </h3>
        <a
          href={waLink(message)}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('landing_page', { cta_placement: 'product_card', lp_category: PAGE_CATEGORY, lead_item_name: glove.name })}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-2.5 text-sm font-black text-white transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
          Cek harga per box
        </a>
      </div>
    </article>
  )
}

export const Route = createFileRoute('/sarung-tangan-latex-nitrile')({
  loader: async () => {
    const [config, categories, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])
    return { config, categories, proofImages, newsPosts }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Latex & Nitrile per Box')
    const description =
      'Sarung tangan latex dan nitrile sekali pakai per box. Shamrock, NAYA, Otoro; powder free dan powdered; warna biru, hitam, pink. MOQ 10 box, kirim dari Surabaya.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: toExternalImage(GLOVES[0].image, CARD_IMAGE_TRANSFORM) },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: toExternalImage(GLOVES[0].image, CARD_IMAGE_TRANSFORM) },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Sarung Tangan Latex & Nitrile', path: PAGE_PATH },
            ]),
          ),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...LATEX_NITRILE_FAQ, ...TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Sarung tangan latex dan nitrile per box di Dropify',
            itemListElement: GLOVES.map((glove, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Thing',
                name: glove.name,
                sku: glove.sku,
                image: toExternalImage(glove.image, CARD_IMAGE_TRANSFORM),
                url: absoluteUrl(`/produk/${glove.slug}`),
              },
            })),
          }),
        },
      ],
    }
  },
  component: LatexNitrileRoute,
})

function LatexNitrileRoute() {
  const { config, categories, proofImages, newsPosts } = Route.useLoaderData()
  return <LatexNitrilePage config={config} categories={categories} proofImages={proofImages} newsPosts={newsPosts} />
}

export function LatexNitrilePage({
  config = null,
  categories = [],
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mau cek sarung tangan sekali pakai. Jenis: ___ (latex/nitrile). Powder free atau powdered: ___. Ukuran: ___. Kebutuhan: ___ box. Kota tujuan: ___. Bisa kirim harga per box dan stoknya?'

  return (
    <div className="min-h-screen bg-[#f7fbfe] text-[#0c2436]">
      <div className="bg-sky-200 px-4 py-2 text-center text-xs font-black text-[#0c2f45] sm:text-sm">
        {GLOVES.length} pilihan latex &amp; nitrile · MOQ 10 box · Kirim dari Surabaya
      </div>

      <header className="border-b border-white/10 bg-[#0c2f45] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Minta quotation per box"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-sky-200/60 px-4 py-2.5 text-sm font-bold text-sky-50 transition-colors hover:bg-sky-200 hover:text-[#0c2f45]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#0c2f45] text-white">
          <div aria-hidden className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-sky-400/10 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:py-20">
            <p className="inline-flex rounded-full bg-sky-200 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#0c2f45]">
              Sekali pakai, dijual per box
            </p>
            <h1 className="mt-6 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Grosir sarung tangan latex dan nitrile, minimum 10 box.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-sky-50/80 md:text-lg">
              Shamrock, NAYA, Otoro, dan lini Disposable, dikirim langsung dari Surabaya. Isi 100 pcs per box untuk hampir semua produk, dengan pilihan powdered, powder free, dan beberapa warna.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <WhatsAppButton
                message={heroMessage}
                label="Minta harga &amp; stok per box"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
              />
              <a href="#pilihan-box" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                Lihat {GLOVES.length} pilihan
                <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section className="bg-[#f7fbfe] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Keputusan pertama</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Latex atau nitrile: pilih dari cara pakainya</h2>
            </div>
            <div className="mt-9 grid gap-4 md:grid-cols-2">
              {MATERIAL_GUIDE.map((item) => (
                <article key={item.title} className="rounded-2xl border border-sky-100 bg-white p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0c2f45] text-sky-200">
                      <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-black tracking-tight">{item.title}</h3>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {item.points.map((point) => (
                      <li key={point} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600">
              Keputusan kedua adalah bedak: powdered memudahkan pemakaian dan pelepasan, powder free menghindari residu bedak. Kalau masih ragu, ceritakan pemakaiannya ke admin lewat WhatsApp.
            </p>
          </div>
        </section>

        <section id="pilihan-box" className="scroll-mt-6 border-y border-sky-100 bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">{`${GLOVES.length} pilihan tersedia`}</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pilihan nitrile dan latex per box</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Klik foto untuk membuka detail produk, atau langsung tanyakan harga per box. Harga mengikuti jumlah, jadi sebutkan total box yang direncanakan.
              </p>
            </div>

            <h3 className="mt-10 text-lg font-black uppercase tracking-wide text-slate-500">Nitrile ({NITRILE_GLOVES.length})</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {NITRILE_GLOVES.map((glove) => (
                <GloveCard key={glove.sku} glove={glove} />
              ))}
            </div>

            <h3 className="mt-12 text-lg font-black uppercase tracking-wide text-slate-500">Latex ({LATEX_GLOVES.length})</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {LATEX_GLOVES.map((glove) => (
                <GloveCard key={glove.sku} glove={glove} />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f7fbfe] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Cara belinya</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">MOQ 10 box, satu chat untuk semua varian</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Minimum order dihitung dari total pesanan, jadi latex dan nitrile boleh digabung dalam satu daftar. Sebutkan produk, ukuran, jumlah box, dan kota tujuan.
              </p>
              <WhatsAppButton
                message={heroMessage}
                label="Kirim daftar kebutuhan"
                placement="moq_section"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <dl className="divide-y divide-sky-100 rounded-2xl border border-sky-100 bg-white px-6">
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Minimum order</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">10 box, boleh kombinasi beberapa varian dalam satu pesanan.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Isi per box</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">100 pcs untuk hampir semua produk. Shamrock Latex Powder Free Black berisi 50 pcs.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Harga</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Dihitung per box mengikuti produk dan jumlah. Quotation dikirim admin lewat WhatsApp.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Pembayaran</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Transfer bank BCA atau Mandiri. Dropify belum melayani COD.</dd>
              </div>
              <div className="grid gap-1 py-5 sm:grid-cols-[200px_1fr] sm:gap-6">
                <dt className="text-sm font-black uppercase tracking-wide text-slate-500">Proses &amp; kirim</dt>
                <dd className="text-sm leading-relaxed text-slate-700 md:text-base">Admin mengonfirmasi waktu penyiapan pesanan. Estimasi perjalanan dihitung dari kota tujuan dan layanan ekspedisi.</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="border-y border-sky-100 bg-white py-14 md:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">Halaman terkait</p>
              <h2 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">Lihat pilihan lain untuk kebutuhan yang berbeda</h2>
            </div>
            <div className="mt-7 grid gap-4 md:grid-cols-3">
              <a href="/sarung-tangan-gekko" className="group rounded-2xl border border-sky-100 bg-[#f7fbfe] p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faHandHoldingMedical} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Sarung tangan kerja Gekko Grip</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">Rajut berlapis nitrile, dipakai berulang, dijual per lusin. Untuk bengkel, pabrik, dan proyek.</p>
              </a>
              <a href="/perlengkapan-medis" className="group rounded-2xl border border-sky-100 bg-[#f7fbfe] p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faBoxOpen} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Perlengkapan medis grosir</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">Paket pengadaan untuk rumah sakit, klinik, dan toko alkes: sarung tangan medis plus masker COMET.</p>
              </a>
              <a href="/supplier-apd-pabrik" className="group rounded-2xl border border-sky-100 bg-[#f7fbfe] p-5 transition-colors hover:border-sky-300">
                <FontAwesomeIcon icon={faDroplet} className="h-5 w-5 text-sky-700" />
                <h3 className="mt-3 font-black tracking-tight group-hover:text-sky-700">Supplier APD pabrik</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">APD tangan dan pernapasan untuk kebutuhan K3 pabrik, termasuk seri COMET berstandar EN.</p>
              </a>
            </div>
          </div>
        </section>

        <section className="bg-[#f7fbfe] py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">FAQ latex &amp; nitrile</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum menentukan isi pesanan Anda</h2>
            </div>
            <div className="mt-9 divide-y divide-sky-100 border-y border-sky-100">
              {LATEX_NITRILE_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-[#0c2436]">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-sky-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-200 py-14 text-[#0c2f45]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Mulai dari daftar kebutuhan</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebutkan varian, ukuran, jumlah box, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">Admin membalas dengan harga per box, foto stok, dan perkiraan ongkir dari Surabaya.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation sarung tangan"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#0c2f45] px-7 py-4 text-base font-black text-white transition-colors hover:bg-[#0c2436]"
            />
          </div>
        </section>

        <BrandTrustSections
          brandName="latex & nitrile"
          itemName="sarung tangan latex dan nitrile per box"
          images={proofImages}
          questions={TRUST_QUESTIONS}
        />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
